import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';

export default function LandingPage() {
  const navigate = useNavigate();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is already logged in, redirect to dashboard
        navigate('/dashboard', { replace: true });
      } else {
        // User is not logged in, show the landing page
        setIsCheckingAuth(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Show a brief loading state while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <svg
            className="animate-spin h-8 w-8 text-primary mx-auto"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-10">
        <div className="container mx-auto px-4 py-4 sm:py-6">
          <nav className="flex items-center justify-between">
            <h1 className="text-xl sm:text-2xl font-display font-bold text-primary">
              Still Love Film
            </h1>
            <Link
              to="/login"
              className="rounded-lg bg-primary px-4 sm:px-6 py-2 text-white font-medium active:bg-primary-800 sm:hover:bg-primary-700 transition-colors min-h-[44px] flex items-center touch-manipulation text-sm sm:text-base"
            >
              Sign In
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-cream via-cream to-primary-50" />

        {/* Decorative elements */}
        <div className="absolute top-20 left-4 sm:left-10 w-24 sm:w-32 h-24 sm:h-32 bg-primary-100 rounded-full blur-3xl opacity-60" />
        <div className="absolute bottom-40 right-4 sm:right-10 w-32 sm:w-48 h-32 sm:h-48 bg-primary-200 rounded-full blur-3xl opacity-40" />

        <div className="relative container mx-auto px-4 pt-20 sm:pt-24 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto text-center">
            {/* Envelope illustration */}
            <div className="mb-6 sm:mb-8 animate-fade-in">
              <div className="relative inline-block">
                {/* Envelope */}
                <div className="relative w-48 h-32 md:w-64 md:h-44 mx-auto">
                  {/* Envelope body */}
                  <div className="absolute inset-0 bg-primary rounded-lg shadow-2xl transform hover:scale-105 transition-transform duration-300">
                    {/* Envelope flap */}
                    <div className="absolute -top-0 left-0 right-0 h-1/2">
                      <div className="absolute inset-0 bg-primary rounded-t-lg" />
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6rem] md:border-l-[8rem] border-r-[6rem] md:border-r-[8rem] border-t-[4rem] md:border-t-[5.5rem] border-l-transparent border-r-transparent border-t-primary-700" />
                    </div>
                    {/* Seal */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 md:w-14 md:h-14 bg-cream rounded-full border-4 border-primary-700 flex items-center justify-center shadow-inner">
                      <span className="text-primary-700 font-display font-bold text-xs md:text-sm">
                        SLF
                      </span>
                    </div>
                    {/* Text on envelope */}
                    <div className="absolute bottom-3 left-0 right-0 text-center">
                      <span className="text-cream/30 font-display text-xs md:text-sm tracking-widest uppercase">
                        Your Weekly Rental
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main headline */}
            <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-display font-bold text-dark mb-4 sm:mb-6 animate-slide-up">
              Rediscover the magic of{' '}
              <span className="text-primary">film night</span>
            </h2>

            {/* Tagline */}
            <p className="text-lg sm:text-xl md:text-2xl text-dark-600 mb-8 sm:mb-10 font-body max-w-2xl mx-auto leading-relaxed">
              Every Friday, receive a surprise film pick from your Letterboxd
              watchlist. Just like opening a DVD rental envelope.
            </p>

            {/* CTA Button */}
            <div className="flex flex-col gap-3 sm:gap-4 justify-center items-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-8 sm:px-10 py-3 sm:py-4 text-base sm:text-lg text-white font-medium active:bg-primary-800 sm:hover:bg-primary-700 transition-all duration-300 shadow-lg sm:hover:shadow-xl sm:hover:scale-105 min-h-[44px] touch-manipulation"
              >
                Get Started
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </Link>
              <span className="text-dark-400 font-body text-xs sm:text-sm">
                Free forever. No credit card required.
              </span>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <svg
            className="w-6 h-6 text-dark-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-10 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-dark mb-3 sm:mb-4">
              How it works
            </h3>
            <p className="text-dark-500 font-body text-base sm:text-lg">
              Three simple steps to your weekly film surprise
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 md:gap-12">
              {/* Step 1 */}
              <div className="relative text-center group">
                <div className="mb-4 sm:mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto group-hover:bg-primary-200 transition-colors duration-300">
                    <span className="text-3xl sm:text-4xl">📝</span>
                  </div>
                  <div className="absolute top-6 right-0 hidden md:block w-full h-0.5 bg-primary-100 -z-10" />
                </div>
                <div className="text-primary font-display font-bold mb-2 text-sm sm:text-base">
                  Step 1
                </div>
                <h4 className="font-display font-bold text-lg sm:text-xl mb-2 sm:mb-3 text-dark">
                  Connect your watchlist
                </h4>
                <p className="text-dark-500 font-body leading-relaxed text-sm sm:text-base">
                  Link your Letterboxd watchlist. We support watchlists and
                  custom lists.
                </p>
              </div>

              {/* Step 2 */}
              <div className="relative text-center group">
                <div className="mb-4 sm:mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto group-hover:bg-primary-200 transition-colors duration-300">
                    <span className="text-3xl sm:text-4xl">📬</span>
                  </div>
                  <div className="absolute top-6 right-0 hidden md:block w-full h-0.5 bg-primary-100 -z-10" />
                </div>
                <div className="text-primary font-display font-bold mb-2 text-sm sm:text-base">
                  Step 2
                </div>
                <h4 className="font-display font-bold text-lg sm:text-xl mb-2 sm:mb-3 text-dark">
                  Receive your rental
                </h4>
                <p className="text-dark-500 font-body leading-relaxed text-sm sm:text-base">
                  Every Friday morning, get an email with a sealed red envelope
                  and a teaser.
                </p>
              </div>

              {/* Step 3 */}
              <div className="relative text-center group">
                <div className="mb-4 sm:mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto group-hover:bg-primary-200 transition-colors duration-300">
                    <span className="text-3xl sm:text-4xl">🎬</span>
                  </div>
                </div>
                <div className="text-primary font-display font-bold mb-2 text-sm sm:text-base">
                  Step 3
                </div>
                <h4 className="font-display font-bold text-lg sm:text-xl mb-2 sm:mb-3 text-dark">
                  Reveal and watch
                </h4>
                <p className="text-dark-500 font-body leading-relaxed text-sm sm:text-base">
                  Open the envelope with a satisfying animation. Discover your
                  film for the weekend.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-20 md:py-32 bg-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-10 sm:mb-16">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-cream mb-3 sm:mb-4">
              Why you&apos;ll love it
            </h3>
            <p className="text-dark-400 font-body text-base sm:text-lg">
              Bringing back the joy of the DVD rental era
            </p>
          </div>

          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
            {/* Feature 1 */}
            <div className="bg-dark-800 rounded-2xl p-5 sm:p-6 md:p-8 hover:bg-dark-700 transition-colors duration-300">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h4 className="font-display font-bold text-lg sm:text-xl mb-2 sm:mb-3 text-cream">
                End decision fatigue
              </h4>
              <p className="text-dark-400 font-body leading-relaxed text-sm sm:text-base">
                Stop scrolling endlessly. Let us pick from films you already
                want to watch.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-dark-800 rounded-2xl p-5 sm:p-6 md:p-8 hover:bg-dark-700 transition-colors duration-300">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
              </div>
              <h4 className="font-display font-bold text-lg sm:text-xl mb-2 sm:mb-3 text-cream">
                Nostalgic experience
              </h4>
              <p className="text-dark-400 font-body leading-relaxed text-sm sm:text-base">
                Remember the excitement of opening a Netflix DVD envelope? We
                brought it back.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-dark-800 rounded-2xl p-5 sm:p-6 md:p-8 hover:bg-dark-700 transition-colors duration-300">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <h4 className="font-display font-bold text-lg sm:text-xl mb-2 sm:mb-3 text-cream">
                Share with others
              </h4>
              <p className="text-dark-400 font-body leading-relaxed text-sm sm:text-base">
                Add family members or friends as recipients. Everyone gets the
                same film pick.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-dark-800 rounded-2xl p-5 sm:p-6 md:p-8 hover:bg-dark-700 transition-colors duration-300">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h4 className="font-display font-bold text-lg sm:text-xl mb-2 sm:mb-3 text-cream">
                Smart selection
              </h4>
              <p className="text-dark-400 font-body leading-relaxed text-sm sm:text-base">
                Our AI avoids repeats and picks films you haven&apos;t seen
                recently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial/Quote Section */}
      <section className="py-16 sm:py-20 md:py-32 bg-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <svg
              className="w-10 h-10 sm:w-12 sm:h-12 text-cream/30 mx-auto mb-4 sm:mb-6"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
            </svg>
            <blockquote className="text-xl sm:text-2xl md:text-3xl font-display text-cream mb-4 sm:mb-6 leading-relaxed">
              Finally, an excuse to stop scrolling and actually watch something
              from my watchlist.
            </blockquote>
            <p className="text-cream/60 font-body text-sm sm:text-base">
              — A film lover, probably
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 sm:py-20 md:py-32 bg-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-display font-bold text-dark mb-4 sm:mb-6">
              Ready for your first rental?
            </h3>
            <p className="text-dark-500 font-body text-base sm:text-lg mb-6 sm:mb-8">
              Sign up in seconds. Connect your watchlist. Get your first film
              pick immediately.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-8 sm:px-10 py-3 sm:py-4 text-base sm:text-lg text-white font-medium active:bg-primary-800 sm:hover:bg-primary-700 transition-all duration-300 shadow-lg sm:hover:shadow-xl sm:hover:scale-105 min-h-[44px] touch-manipulation"
            >
              Get Started Free
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark py-8 sm:py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
            <div className="text-center md:text-left">
              <h4 className="text-lg sm:text-xl font-display font-bold text-cream mb-1 sm:mb-2">
                Still Love Film
              </h4>
              <p className="text-dark-400 text-xs sm:text-sm font-body">
                Your weekly film pick, delivered with love.
              </p>
            </div>
            <div className="text-center md:text-right">
              <p className="text-dark-400 text-xs sm:text-sm font-body">
                &copy; {new Date().getFullYear()} Still Love Film. Made for film
                lovers everywhere.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
